import { createContextFactory, useBooleanState } from '@mfw/components';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { isDefined, resolveBrandingSource } from '@mfw/common';
import type { BrandingSource } from '@mfw/common';
import React from 'react';

import type {
  SetPreferredOrganizationArgs,
  FetchOrganizationsArgs,
} from '../../api/types/organizations.types';
import type { UiOrganizationUser } from '../../schemas/organizationUser/organization.user.schema';
import type { ApiCorpOrganization } from '../../schemas/corpOrganizations.schema';
import type { UiOrganization } from '../../schemas/organizations.schema';
import type { Maybe } from '../../types/maybe.types';

import {
  useInfiniteSwitcherOrganizations,
  useSetPreferredOrganization,
} from '../../hooks/organizations';
import { useAccountScopeReset } from '../accountScope';
import { useAppThemeContext } from '../AppThemeProvider';
import { useAuthContext } from '../AuthProvider';
import { reconcileCurrentOrganization } from './reconcileCurrentOrganization';
import { pickOrgMembership } from './pickOrgMembership';
import { queryKeys } from '../../queryKeys';

export const DEFAULT_ORGANIZATION_PROVIDER_FETCH_OPTIONS = {
  orderBy: 'name',
  orderDirection: 'asc',
  status: 'active',
} as FetchOrganizationsArgs['filters'];

type OrganizationContextType = {
  /**
   * Fetches next page of organizations infinite spinner
   */
  fetchNextPage: ReturnType<
    typeof useInfiniteSwitcherOrganizations
  >['fetchNextPage'];
  organizations?: ReturnType<typeof useInfiniteSwitcherOrganizations>['data'];
  selectOrganization: ({ id }: SetPreferredOrganizationArgs) => Promise<void>;
  /**
   * Sets search term in context
   */
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  /**
   * The corp organization the current user belongs to, if any. Surfaced from
   * the `/me` payload so it can be shown in the organization switcher.
   */
  currentCorpOrganization?: Maybe<ApiCorpOrganization>;
  currentOrganization?: Maybe<UiOrganization>;
  /**
   * Rebrand (D-9): the resolved branding source in effect — org, corp, or
   * neither. A selected organization with an active rebrand wins; otherwise
   * the corp organization applies while corp mode is active AND the corp
   * organization's status is ACTIVE; otherwise `undefined`, which means
   * tenant branding. This is the ONLY branding value other files should read
   * — they must not re-derive it from an organization or corp object.
   */
  brandingSource?: BrandingSource;
  currentOrgUser?: UiOrganizationUser;
  /**
   * Enters "corp mode": selects the user's corp organization instead of a
   * regular organization. Clears the current (regular) organization so the
   * app renders the restricted corp navigation.
   */
  selectCorpOrganization: () => void;
  /**
   * F3: enters the org-less "personal profile" context — clears the selected
   * organization and persists `lastUsedContext: 'personal'` server-side.
   */
  selectPersonalProfile: () => void;
  /**
   * Deselects organization.
   */
  clearOrganization: () => void;
  /**
   * Invalidates the account-scoped switcher list and every other organization list.
   *
   * Exposed for the callers that change the account's MEMBERSHIPS without going through
   * `selectOrganization` — which reaches the same invalidation on its way to the PATCH.
   * `AcceptInvitePage` is the one such caller: the organization it was just admitted to is
   * not in the list this account already fetched, and `OrganizationGuard` bounces on that
   * list rather than on the user snapshot.
   */
  clearOrganizationCache: () => Promise<void>;
  /**
   * Merges the reconciled fields of a freshly fetched organization row into
   * the `currentOrganization` snapshot (LB1-1). The snapshot otherwise updates
   * only from the switcher list, whose search-filtered pages may not hold the
   * current organization at all — a flow that has just PROVEN a change (the
   * successful pay-now poll clearing `billingLocked`) hands the fresh row in
   * here instead of hoping the list happens to refetch and contain it.
   */
  reconcileOrganizationRow: (row: Maybe<UiOrganization>) => void;
  /**
   * F3: whether the user is currently in the org-less "personal profile"
   * context (no organization selected and not in corp mode).
   */
  isPersonalMode: boolean;
  /**
   * Whether the user is currently viewing their corp organization context.
   */
  isCorpMode: boolean;
  /**
   * D14: whether the selected organization is a single trainer org — a solo
   * trainer's profile (organization type `TRAINER`), which has exactly one
   * trainer. Anything that would only repeat that one trainer (assigned
   * trainers, a Trainers column) reads this instead of the type.
   */
  isSingleTrainerOrg: boolean;
  isLoading: boolean;
};

const CORP_MODE_STORAGE_PREFIX = 'mfw:corpMode:';

const OrganizationContext = createContextFactory({
  devToolsName: 'OrganizationContext',
  initialValue: {} as OrganizationContextType,
});

export interface OrganizationProviderProps {
  children: React.ReactNode;
}
const OrganizationProvider = ({ children }: OrganizationProviderProps) => {
  const queryClient = useQueryClient();
  /**
   * Makes sure that last selected organization is not selected again
   * in case that user has manually removed organization from dropdown.
   */
  const organizationClearedRef = React.useRef(false);
  /**
   * Restores the persisted corp-mode choice once per user — see the effect that reads it.
   * Declared up here with the other one-shot guard because the account boundary below has
   * to release both.
   */
  const corpModeInitializedForRef = React.useRef<string | undefined>(undefined);

  const {
    currentUser,
    isAuthenticated,
    isLoading: isAuthLoading,
  } = useAuthContext();
  const { mutateAsync } = useSetPreferredOrganization();
  const [search, setSearch] = React.useState('');

  const { organizationId } = useParams();

  const [currentOrganization, setCurrentOrganization] =
    React.useState<Maybe<UiOrganization>>(undefined);

  const currentCorpOrganization = currentUser?.corpOrganization ?? undefined;
  const hasCorpOrganization = Boolean(currentCorpOrganization?.id);

  const isCorpMode = useBooleanState();

  /**
   * THE ACCOUNT BOUNDARY (PR-2.1). Everything below this line is state one account built,
   * and this provider is no longer remounted every time the identity moves: a login in
   * the same route branch, a confirmed credential transition, the anonymous
   * accept-invite login and a super admin's tenant move all leave it mounted.
   *
   * Corp mode is turned off WITHOUT persisting: the stored choice is keyed per user and
   * belongs to the account that made it, which may well come back. Both one-shot refs are
   * released so the per-account initialization effects below run again for whoever
   * arrived — that is the whole point of clearing them.
   *
   * It runs during the render that first sees the new account, not in an effect, so no
   * committed render can pair the new `currentUser` with the previous selection (it would
   * hand them a `currentOrgUser` role in a gym they did not choose).
   */
  const { accountKey, accountEpoch } = useAccountScopeReset(() => {
    setCurrentOrganization(undefined);
    setSearch('');
    isCorpMode.off();
    organizationClearedRef.current = false;
    corpModeInitializedForRef.current = undefined;
  });
  /**
   * Corp selection is not persisted server-side (`lastUsedOrganizationId` is an
   * FK to a regular organization), so we remember the choice locally per user.
   */
  const persistCorpMode = React.useCallback(
    (value: boolean) => {
      const userId = currentUser?.id;
      if (userId) {
        localStorage.setItem(
          `${CORP_MODE_STORAGE_PREFIX}${userId}`,
          String(value),
        );
      }
    },
    [currentUser?.id],
  );

  const currentOrgUser = React.useMemo(
    () =>
      pickOrgMembership(
        currentUser?.organizationUsers,
        currentOrganization?.id,
      ),
    [currentUser, currentOrganization],
  );
  const memoizedSwitcherArgs = React.useMemo(() => {
    return {
      ...DEFAULT_ORGANIZATION_PROVIDER_FETCH_OPTIONS,
      search,
    };
  }, [search]);
  /**
   * WHETHER THE SWITCHER MAY ASK AT ALL.
   *
   * The switcher must list every org the user can switch INTO, in ALL contexts —
   * including the org-less personal profile. `/auth/me/organizations` is a self-scoped
   * route (auth only, membership-filtered server-side), so it must NOT be gated on
   * `organizations_read`: that permission is context-scoped and absent in personal mode,
   * which previously left the switcher unable to return to any organization.
   *
   * Named rather than inlined because the loading mask below has to read the same answer:
   * a query that is switched off is not a query that is loading.
   */
  const isSwitcherEnabled = isAuthenticated && Boolean(currentUser?.id);
  const {
    data: switcherOrganizations,
    isPlaceholderData,
    fetchNextPage,
    isLoading,
  } = useInfiniteSwitcherOrganizations(
    memoizedSwitcherArgs,
    isSwitcherEnabled,
    accountKey,
  );

  /**
   * WHOSE ROWS ARE ON SCREEN. The account is in the query key above, but
   * `hooks/base/useInfiniteQuery` keeps the previous key's result as placeholder data —
   * deliberately, so searching and paging the switcher does not blank it. Across an
   * ACCOUNT boundary that previous result is a stranger's list, and the seeding effect
   * further down would hand its first row to whoever just signed in.
   *
   * So the rows are published only once a fetch has SETTLED under the current account
   * key. Placeholder renders leave this untouched, which is exactly what keeps a
   * same-account search from blanking the list.
   */
  const [listedAccountKey, setListedAccountKey] = React.useState<
    string | undefined
  >(undefined);
  React.useEffect(() => {
    if (isLoading || isPlaceholderData) return;
    setListedAccountKey(accountKey);
  }, [accountKey, isLoading, isPlaceholderData]);
  const organizations =
    listedAccountKey === accountKey ? switcherOrganizations : undefined;

  /**
   * Between two accounts the previous rows are gone and the new ones are not in yet, and
   * the query itself may already report `isLoading: false` because it is serving
   * placeholder data. Consumers are told LOADING for that window — the switcher owes the
   * new account a skeleton, not the claim that they belong to no organization.
   *
   * Unless the query is switched OFF, which is the one state that window cannot leave on
   * its own. A disabled observer still serves the previous key's rows as placeholder data
   * — `isLoading` false, `isPlaceholderData` true — so the settle effect above never runs
   * for the new key and `listedAccountKey` stays where the account that left put it. The
   * mask would then latch `true` for the rest of the mount, and every consumer would
   * render a skeleton for a fetch that is never coming. Nothing is in flight, so nothing
   * is loading; the rows stay gated on the settle effect either way, which is what keeps
   * the placeholder list from being published under the new key.
   */
  const isSwitcherLoading =
    isSwitcherEnabled && (isLoading || listedAccountKey !== accountKey);

  /**
   * …and the cached lists of the account that left go with it. The switcher's key now
   * carries the account, but `fetchOrganizations` is shared by every organization list in
   * the app and keys on filters alone, so without this the next account reads the
   * previous one's rows out of the cache. Keyed on the EPOCH, not on the key: the key
   * necessarily moves once on a cold boot, as the tenant and then the account settle, and
   * clearing there would throw away the list this very mount is about to use.
   *
   * A LAYOUT effect on purpose, and it must stay one: layout effects run before
   * `useSyncExternalStore` subscribes, so the sweep removes the query React Query built
   * during this very render and the observer's passive subscription then rebuilds it and
   * fetches it. Moved to `useEffect` the order reverses — the observer is already
   * subscribed when the sweep lands, and removing the query underneath it detaches the
   * switcher's first fetch for the new account from the cache.
   */
  const clearedForEpochRef = React.useRef(accountEpoch);
  React.useLayoutEffect(() => {
    if (clearedForEpochRef.current === accountEpoch) return;
    clearedForEpochRef.current = accountEpoch;
    queryClient.removeQueries({
      queryKey: queryKeys.organizations.fetchInfiniteSwitcherOrganizations._def,
    });
    queryClient.removeQueries({
      queryKey: queryKeys.organizations.fetchOrganizations._def,
    });
  }, [accountEpoch, queryClient]);

  const clearOrganizationCache = React.useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey:
          queryKeys.organizations.fetchInfiniteSwitcherOrganizations._def,
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.fetchOrganizations._def,
      }),
    ]);
  }, [queryClient]);

  const selectOrganization = React.useCallback(
    async ({ id }: SetPreferredOrganizationArgs) => {
      isCorpMode.off();
      persistCorpMode(false);
      organizationClearedRef.current = false;
      // Rebrand: snapshot the branding fields from the switcher row BEFORE
      // invalidating the list. The /set-preferred-organization response is a
      // raw org stripped of the computed `rebrandActive` (and the signed logo
      // lives in `logoUrl`/`logoName` differently per endpoint), so without
      // this the stored org would carry no branding and the theme/logo would
      // silently revert to the tenant as soon as the live switcher list is
      // searched/paginated away from this org.
      const brandingSnapshot = organizations?.data?.find(
        (organization) => organization?.id === id,
      );
      clearOrganizationCache();
      const organization = await mutateAsync({ id, context: 'organization' });
      setCurrentOrganization(
        organization
          ? {
              ...organization,
              logoName: brandingSnapshot?.logoName ?? organization.logoName,
              logoUrl: brandingSnapshot?.logoUrl ?? organization.logoUrl,
              rebrandActive:
                brandingSnapshot?.rebrandActive ?? organization.rebrandActive,
              theme: brandingSnapshot?.theme ?? organization.theme,
            }
          : organization,
      );
    },
    [
      clearOrganizationCache,
      isCorpMode,
      mutateAsync,
      organizations,
      persistCorpMode,
    ],
  );

  /**
   * Enters corp mode. The corp organization is not a regular organization, so
   * we clear the current organization and let the UI navigate to the corp view.
   */
  const selectCorpOrganization = React.useCallback(() => {
    organizationClearedRef.current = true;
    isCorpMode.on();
    persistCorpMode(true);
    setCurrentOrganization(undefined);
  }, [isCorpMode, persistCorpMode]);

  const clearOrganization = React.useCallback(() => {
    organizationClearedRef.current = true;
    isCorpMode.off();
    persistCorpMode(false);
    clearOrganizationCache();
    setCurrentOrganization(undefined);
  }, [isCorpMode, persistCorpMode, clearOrganizationCache]);

  /**
   * F3: enters the org-less "personal profile" context. Deselects the current
   * organization locally and persists `lastUsedContext: 'personal'`
   * server-side — `lastUsedOrganizationId` is left untouched so it still
   * reflects the last org used when switching back.
   */
  const selectPersonalProfile = React.useCallback(async () => {
    clearOrganization();
    await mutateAsync({ context: 'personal' });
  }, [clearOrganization, mutateAsync]);

  /**
   * Corp mode falls back to normal navigation if the corp link is gone
   * (e.g. the user was offboarded and `corpOrganization` is now null).
   */
  const isCorpModeActive = isCorpMode.isActive && hasCorpOrganization;

  /**
   * Restores the persisted corp-mode choice once per user. When the user has a
   * corp organization but no preferred regular organization, corp mode is the
   * default landing context.
   */
  React.useEffect(() => {
    const userId = currentUser?.id;
    if (!userId || corpModeInitializedForRef.current === userId) return;
    corpModeInitializedForRef.current = userId;
    if (!hasCorpOrganization) {
      isCorpMode.off();
      return;
    }
    const stored = localStorage.getItem(`${CORP_MODE_STORAGE_PREFIX}${userId}`);
    if (
      stored === 'true' ||
      (stored === null && !currentUser?.lastUsedOrganization)
    ) {
      organizationClearedRef.current = true;
      isCorpMode.on();
      setCurrentOrganization(undefined);
    }
  }, [
    currentUser?.id,
    currentUser?.lastUsedOrganization,
    hasCorpOrganization,
    isCorpMode,
  ]);

  React.useEffect(() => {
    if (
      !isAuthenticated ||
      organizationClearedRef.current ||
      isCorpModeActive ||
      isLoading ||
      !organizations?.total ||
      currentOrganization?.id
    )
      return;
    // F3: the user's last choice was the personal profile — do not seed an
    // organization, personal mode stays active until explicitly changed.
    if (currentUser?.lastUsedContext === 'personal') {
      organizationClearedRef.current = true;
      return;
    }
    // Platform users are never org members; the org-less personal profile is
    // their authoring/curation workspace (premium content), so they land
    // there instead of in the first organization they can merely see.
    if (currentUser?.isPlatformUser) {
      organizationClearedRef.current = true;
      return;
    }
    if (currentUser?.lastUsedOrganization) {
      setCurrentOrganization(currentUser?.lastUsedOrganization);
    } else if (
      !currentUser?.lastUsedOrganization &&
      organizations?.data?.[0]?.id
    ) {
      setCurrentOrganization(organizations?.data?.[0]);
    }
  }, [
    isLoading,
    currentOrganization,
    isAuthenticated,
    isCorpModeActive,
    currentUser?.lastUsedContext,
    currentUser?.lastUsedOrganization,
    currentUser?.isPlatformUser,
    organizations,
    organizationId,
  ]);

  /**
   * …and once one IS selected, keep its display/branding fields current. The
   * seeding effect above deliberately stops running at that point, which left
   * the snapshot unable to take an UPDATE: changing the organization logo
   * refreshed the org page (its own query is invalidated) while the sidebar
   * switcher trigger and the rebrand logo — both readers of this snapshot —
   * kept the old picture until a hard refresh.
   *
   * `reconcileCurrentOrganization` returns the same object when nothing
   * relevant changed, so this cannot loop, and it keeps the snapshot untouched
   * when the (search-filtered, paginated) list has no row for it.
   */
  React.useEffect(() => {
    setCurrentOrganization((previous) =>
      reconcileCurrentOrganization(previous, organizations?.data),
    );
  }, [organizations]);

  // Same reconcile rules, but fed a single row a caller has just fetched —
  // see the context type docblock for why the switcher list cannot be the
  // only writer of this snapshot.
  const reconcileOrganizationRow = React.useCallback(
    (row: Maybe<UiOrganization>) => {
      setCurrentOrganization((previous) =>
        reconcileCurrentOrganization(previous, [row]),
      );
    },
    [],
  );

  /**
   * The seeding effect above runs AFTER the render in which the list settles,
   * so that render commits with no organization yet — and every consumer reads
   * it as the personal profile: the sidebar flashed the client/personal rows
   * before swapping in the organization's. This is the same decision the
   * effect is about to make, answered during render, so `isLoading` covers
   * that window. Keep the two in step: a condition the effect bails out on and
   * this does not would latch the mask `true`.
   */
  const isSeedingOrganization =
    isAuthenticated &&
    !organizationClearedRef.current &&
    !isCorpModeActive &&
    !isLoading &&
    Boolean(organizations?.total) &&
    !currentOrganization?.id &&
    currentUser?.lastUsedContext !== 'personal' &&
    !currentUser?.isPlatformUser &&
    Boolean(currentUser?.lastUsedOrganization || organizations?.data?.[0]?.id);

  /**
   * F3: the org-less "personal profile" context is active whenever there is
   * no selected organization and the user isn't in corp mode.
   */
  const isPersonalMode =
    isAuthenticated && !isCorpModeActive && !currentOrganization?.id;

  /**
   * Rebrand: the organization branding candidate. Prefer the stored
   * `currentOrganization` whenever it already carries branding — i.e. when
   * `rebrandActive` is present (a boolean). Every path that sets it now
   * snapshots the branding: the lastUsedOrganization seed (from `/auth/me`),
   * the switcher-row seed, and the select-time snapshot above. This makes the
   * result INDEPENDENT of the live, search-filtered switcher list, so a later
   * search/pagination can never drop the active org off the loaded pages and
   * silently revert the theme. The live-list lookup is only a defensive
   * fallback for any org seeded without the computed fields.
   */
  const brandingOrganizationRecord = React.useMemo(() => {
    if (!currentOrganization?.id) return undefined;
    if (isDefined(currentOrganization.rebrandActive)) {
      return currentOrganization;
    }
    return (
      organizations?.data?.find(
        (organization) => organization?.id === currentOrganization.id,
      ) ?? currentOrganization
    );
  }, [organizations, currentOrganization]);

  /**
   * Rebrand: the branding level in effect (D-9). The org branch keeps the
   * existing snapshot behavior; corp joins as a second candidate, gated on
   * corp mode AND an ACTIVE corp organization — a FROZEN one falls back to
   * tenant branding (D-10), mirroring how freezing already revokes corp
   * premium.
   */
  const brandingSource = React.useMemo(
    () =>
      resolveBrandingSource(
        {
          active: brandingOrganizationRecord?.rebrandActive === true,
          theme: brandingOrganizationRecord?.theme,
          logoUrl:
            brandingOrganizationRecord?.logoUrl ??
            brandingOrganizationRecord?.logoName,
        },
        {
          active:
            isCorpModeActive && currentCorpOrganization?.status === 'ACTIVE',
          theme: currentCorpOrganization?.theme,
          logoUrl: currentCorpOrganization?.logoUrl,
        },
      ),
    [brandingOrganizationRecord, currentCorpOrganization, isCorpModeActive],
  );

  /**
   * Rebrand bridge: OrganizationProvider mounts INSIDE AppThemeProvider, so we
   * do not invert the hierarchy — instead we push the resolved theme down via
   * the theme context setter. `mergeOrgTheme` (called once inside
   * AppThemeProvider) does the actual merge. Passing `undefined` when nothing
   * is branded reverts to the tenant theme without leaving stale org vars.
   */
  const { setOrgTheme, settleOrgBranding } = useAppThemeContext();
  React.useEffect(() => {
    setOrgTheme(brandingSource?.theme);
  }, [brandingSource?.theme, setOrgTheme]);

  /**
   * …and says when that theme is the one this load keeps: once the session,
   * `/auth/me` and the switcher (with the seeding of a first organization)
   * have answered. Until then AppThemeProvider keeps the cached branding up
   * rather than repainting each intermediate layer.
   */
  const isBrandingSettled =
    !isAuthLoading && !isSwitcherLoading && !isSeedingOrganization;
  React.useEffect(() => {
    if (!isBrandingSettled) return;
    settleOrgBranding(brandingSource?.logoUrl);
  }, [brandingSource?.logoUrl, isBrandingSettled, settleOrgBranding]);

  const providedValue = React.useMemo(() => {
    return {
      brandingSource,
      clearOrganization,
      clearOrganizationCache,
      currentCorpOrganization,
      currentOrgUser,
      currentOrganization,
      fetchNextPage,
      isCorpMode: isCorpModeActive,
      isLoading: isSwitcherLoading || isSeedingOrganization,
      isPersonalMode,
      isSingleTrainerOrg: currentOrganization?.type === 'TRAINER',
      organizations,
      reconcileOrganizationRow,
      selectCorpOrganization,
      selectOrganization,
      selectPersonalProfile,
      setSearch,
    };
  }, [
    organizations,
    isSwitcherLoading,
    isSeedingOrganization,
    isPersonalMode,
    brandingSource,
    clearOrganization,
    clearOrganizationCache,
    currentCorpOrganization,
    isCorpModeActive,
    reconcileOrganizationRow,
    selectCorpOrganization,
    selectOrganization,
    selectPersonalProfile,
    setSearch,
    fetchNextPage,
    currentOrganization,
    currentOrgUser,
  ]);
  return (
    <OrganizationContext.Provider value={providedValue}>
      {children}
    </OrganizationContext.Provider>
  );
};

export function useOrganizationContext() {
  return OrganizationContext.useContext();
}
export default OrganizationProvider;
